import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast as sonnerToast } from 'sonner';

import api from '@/app/_module/services/api';
import { getApiUrl } from '@/app/_module/services/config';
import { endpoint } from '@/app/_module/services/endpoints';
import { handleApiCall } from '@/app/_module/utils/apiResponseHandler';
import { useUserContext } from '@/app/_module/store/contexts/userContext/userContexts';

import type { LeadAssignmentRequest, LeadAssignmentResponse } from '../types/leadassign-types';

// Extended request that carries lead statuses for optimistic byStatus updates
interface AssignLeadMutationInput extends LeadAssignmentRequest {
  /** Status of each lead being assigned (e.g. "New lead", "Appt") for optimistic byStatus updates */
  leadStatuses?: string[];
  /** When true, hides previous notes/calls/activity so the lead appears fresh */
  cleanSlate?: boolean;
}

// Backend caps each assign call at 2,000 leads (MAX_BULK_ASSIGN — keep equal).
// Frontend keeps that per-request cap but splits larger selections into
// sequential batches so users uploading 5k+ lists can assign all of them
// without manually chunking.
const BACKEND_BATCH_SIZE = 2000;
// A full batch can outlast the global 60s axios timeout on a busy DB; the
// browser giving up while the server commits reads as a false "failed".
// Stays under Cloudflare's 100s proxy limit.
const ASSIGN_TIMEOUT_MS = 90_000;
// Safety ceiling on a single user action. Anything above this almost always
// indicates a mistake (e.g. "Select all" on a 200k list) — we refuse up front
// instead of firing 200 sequential requests.
const MAX_BULK_ASSIGN_TOTAL = 25000; // = the CSV/Excel import cap (MAX_IMPORT_ROWS)

export const useAssignLead = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const agencyId = user.currentAgency?.agency?.id;

  return useMutation<LeadAssignmentResponse, Error, AssignLeadMutationInput, { previousTracking: unknown; previousWorkload: unknown }>({
    mutationFn: async (data: AssignLeadMutationInput) => {
      const total = data.leadIds?.length ?? 0;
      if (total === 0) {
        throw new Error('No leads selected to assign.');
      }
      if (total > MAX_BULK_ASSIGN_TOTAL) {
        throw new Error(
          `You can assign at most ${MAX_BULK_ASSIGN_TOTAL.toLocaleString()} leads in a single action. You selected ${total.toLocaleString()} — please narrow the selection.`,
        );
      }

      // Small selections: single request, no progress toast.
      if (total <= BACKEND_BATCH_SIZE) {
        const response = await handleApiCall(async () => await api.post({
          url: getApiUrl(endpoint.leadAssignment.assignLead),
          body: {
            leadIds: data.leadIds,
            agentId: data.agentId,
            ...(data.cleanSlate && { cleanSlate: true }),
          },
          auth: true,
          options: { timeout: ASSIGN_TIMEOUT_MS },
        }));
        return response as LeadAssignmentResponse;
      }

      // Large selections: auto-split into sequential batches of BACKEND_BATCH_SIZE.
      // Sequential (not Promise.all) keeps DB lock contention low and makes partial
      // failures resumable — if batch 3 fails, batches 1-2 are already committed
      // so we can report exact progress and surface which ids still need assigning.
      const toastId = sonnerToast.loading('Assigning leads', {
        description: `0 of ${total.toLocaleString()} assigned…`,
      });

      const failedLeadIds: string[] = [];
      let assignedSoFar = 0;
      let lastResponse: any = null;
      // The server's own counts, summed over batches (each answer only covers its slice).
      const totals = { assignedCount: 0, householdMembersAdded: 0, skippedCount: 0, skippedLeads: [] as string[], skippedLeadIds: [] as string[] };
      // Capture the backend's actual reason for the most-recent batch
      // failure so the onError consumer can show the real message instead
      // of a generic "All X failed". When the agent is at their lead-limit
      // cap, the backend already returns a rich human-readable sentence
      // ("Hadi Osseili is on the trial plan (cap: 1,000 leads)…"); this
      // makes sure that text actually reaches the user's toast.
      let lastBatchErrorMessage: string | null = null;

      for (let i = 0; i < total; i += BACKEND_BATCH_SIZE) {
        const slice = data.leadIds.slice(i, i + BACKEND_BATCH_SIZE);
        try {
          lastResponse = await handleApiCall(async () => await api.post({
            url: getApiUrl(endpoint.leadAssignment.assignLead),
            body: {
              leadIds: slice,
              agentId: data.agentId,
              ...(data.cleanSlate && { cleanSlate: true }),
            },
            auth: true,
            options: { timeout: ASSIGN_TIMEOUT_MS },
          }));
          assignedSoFar += slice.length;
          const r = lastResponse?.data ?? lastResponse ?? {};
          totals.assignedCount += Number(r.assignedCount ?? slice.length) || 0;
          totals.householdMembersAdded += Number(r.householdMembersAdded) || 0;
          totals.skippedCount += Number(r.skippedCount) || 0;
          if (Array.isArray(r.skippedLeads)) totals.skippedLeads.push(...r.skippedLeads);
          if (Array.isArray(r.skippedLeadIds)) totals.skippedLeadIds.push(...r.skippedLeadIds);
        } catch (batchErr: any) {
          // Record failure but keep pressing — owners usually want as many
          // successful assignments as possible, not an all-or-nothing rollback.
          failedLeadIds.push(...slice);
          // Capture the SPECIFIC backend message so the consumer can show
          // "Hadi Osseili is on the trial plan (cap: 1,000)…" instead of
          // the previous generic "All N assignments failed."
          const msg = batchErr?.message || batchErr?.response?.data?.message;
          if (typeof msg === 'string' && msg.trim()) {
            lastBatchErrorMessage = msg;
          }
        }
        sonnerToast.loading('Assigning leads', {
          id: toastId,
          description: `${assignedSoFar.toLocaleString()} of ${total.toLocaleString()} assigned${failedLeadIds.length > 0 ? ` · ${failedLeadIds.length.toLocaleString()} failed` : ''}…`,
        });
      }

      sonnerToast.dismiss(toastId);

      if (failedLeadIds.length > 0 && assignedSoFar === 0) {
        // ALL batches failed. If the backend gave us a specific reason
        // (lead-limit, capacity, etc.), throw with THAT so onError can
        // render it verbatim. Fall back to the generic only if we somehow
        // got no message at all.
        throw new Error(lastBatchErrorMessage || `All ${total.toLocaleString()} assignments failed. Please try again.`);
      }
      if (failedLeadIds.length > 0) {
        // Partial success — surface accurate counts in the downstream success toast
        // and the big green banner. `assignedCount` is what the component reads first.
        // Include the backend reason on the warning so the agent knows WHY
        // the remaining batches were rejected (almost always lead-limit).
        sonnerToast.warning(
          `${assignedSoFar.toLocaleString()} assigned, ${failedLeadIds.length.toLocaleString()} failed`,
          {
            description: lastBatchErrorMessage
              ? lastBatchErrorMessage
              : 'The failed leads were not assigned. Try again for just those.',
          },
        );
        return ({ ...totals, totalAssigned: totals.assignedCount, failedCount: failedLeadIds.length } as unknown as LeadAssignmentResponse);
      }
      // Same flat shape a single request returns, with every batch's counts added up.
      return (totals as unknown as LeadAssignmentResponse);
    },
    onMutate: async (data) => {
      // Optimistically update status-tracking summary so cards update instantly
      await queryClient.cancelQueries({ queryKey: ['status-tracking'] });
      await queryClient.cancelQueries({ queryKey: ['agentWorkload'] });

      const previousTracking = queryClient.getQueryData(['status-tracking']);
      // Use exact key match: ["agentWorkload", agencyId]
      const workloadKey = ['agentWorkload', agencyId];
      const previousWorkload = queryClient.getQueryData(workloadKey);

      const count = data.leadIds.length;

      queryClient.setQueryData(['status-tracking'], (old: any) => {
        if (!old?.summary) return old;

        // Optimistically update byStatus counts if we know the lead statuses
        const updatedByStatus = old.byStatus ? { ...old.byStatus } : {};
        if (data.leadStatuses) {
          for (const status of data.leadStatuses) {
            const key = status || 'New lead';
            updatedByStatus[key] = (updatedByStatus[key] || 0) + 1;
          }
        }

        return {
          ...old,
          summary: {
            ...old.summary,
            assigned: (old.summary.assigned || 0) + count,
            unassigned: Math.max(0, (old.summary.unassigned || 0) - count),
          },
          byStatus: updatedByStatus,
        };
      });

      // Optimistically update agent workload so agent performance updates instantly
      queryClient.setQueryData(workloadKey, (old: any) => {
        if (!old?.agents) return old;
        return {
          ...old,
          agents: old.agents.map((agent: any) => {
            if (agent.id === data.agentId || agent.agentId === data.agentId) {
              return {
                ...agent,
                currentLeads: (agent.currentLeads || 0) + count,
                activeLeadsCount: (agent.activeLeadsCount || 0) + count,
              };
            }
            return agent;
          }),
        };
      });

      return { previousTracking, previousWorkload };
    },
    onError: (_err, _data, context) => {
      // Roll back optimistic updates on error
      if (context?.previousTracking) {
        queryClient.setQueryData(['status-tracking'], context.previousTracking);
      }
      if (context?.previousWorkload) {
        queryClient.setQueryData(['agentWorkload', agencyId], context.previousWorkload);
      }
    },
    onSettled: async () => {
      // 1. Invalidate non-workload queries immediately
      // ⚠️ refetchType 'all', not the default 'active'.
      //
      // React Query only refetches ACTIVE queries by default. The unassigned list
      // sits behind the assign modal, and while that modal is open its observer
      // can be unmounted — so the invalidation marked it stale and then nothing
      // happened. The list only caught up on the next 120s poll, which is exactly
      // the "assigned Zeinab's leads, still shows unassigned, came back 2 minutes
      // later and it was fine" report. Refetching inactive queries too costs one
      // request and removes the whole class of "I have to leave and come back".
      const refetchAll = { refetchType: 'all' as const };
      queryClient.invalidateQueries({ queryKey: ['unassignedLeads'], ...refetchAll });
      queryClient.invalidateQueries({ queryKey: ['allLeads'], ...refetchAll });
      queryClient.invalidateQueries({ queryKey: ['agentLeads'], ...refetchAll }); // per-agent drill-in (AgentWorkload row expand)
      queryClient.invalidateQueries({ queryKey: ['leads'], ...refetchAll });
      queryClient.invalidateQueries({ queryKey: ['lead-stats'], ...refetchAll });
      queryClient.invalidateQueries({ queryKey: ['status-tracking'], ...refetchAll });

      // 2. Fetch workload with ?fresh=true to bypass ALL backend caches
      //    This ensures the DB is queried directly for accurate counts.
      //    We inject the result directly into the query cache so the optimistic
      //    update is replaced with real data (not stale cache).
      try {
        const freshUrl = getApiUrl(endpoint.agency.getAgentsWorkload(agencyId!)) + '?limit=1000&fresh=true';
        const freshResult: any = await handleApiCall(async () =>
          api.get({ url: freshUrl, auth: true })
        );

        // Extract agents array from response (same logic as useAgentWorkload queryFn)
        let agents: any[] = [];
        if (Array.isArray(freshResult)) {
          agents = freshResult;
        } else if (freshResult?.data?.data?.items) {
          agents = freshResult.data.data.items;
        } else if (freshResult?.data?.items) {
          agents = freshResult.data.items;
        } else if (freshResult?.data && Array.isArray(freshResult.data)) {
          agents = freshResult.data;
        } else if (freshResult?.items) {
          agents = freshResult.items;
        } else {
          const d = freshResult?.data || freshResult || {};
          agents = d.items || d.agents || (Array.isArray(d) ? d : []);
        }

        const formatTime = (t: any): string => {
          if (typeof t === 'string') return t;
          if (typeof t === 'number') {
            if (t === 0) return '0m';
            if (t < 60) return `${Math.round(t)}s`;
            return `${Math.round(t / 60)}m`;
          }
          return '0m';
        };

        const mapped = agents.map((a: any) => {
          const uid = a.id || a.userId || a.agentId;
          return {
            id: uid,
            agentId: uid,
            name: a.name || a.agentName || 'Unknown Agent',
            agentName: a.name || a.agentName || 'Unknown Agent',
            email: a.email || '',
            agencyId: a.agencyId || undefined,
            agencyName: a.agencyName || undefined,
            currentLeads: a.currentLeads || a.activeLeadsCount || 0,
            activeLeadsCount: a.currentLeads || a.activeLeadsCount || 0,
            ownedLeads: a.ownedLeads ?? 0,
            completedLeads: a.completedLeads || 0,
            maxLeads: a.maxLeads || 0,
            leadGoal: a.leadGoal ?? undefined,
            status: a.status || 'active',
            lastActivity: a.lastActivity || a.lastActivityAt || undefined,
            conversionRate: a.conversionRate || 0,
            avgResponseTime: formatTime(a.avgResponseTime),
            assignedBreakdown: Array.isArray(a.assignedBreakdown) ? a.assignedBreakdown : [],
          };
        });

        // Inject fresh data directly into query cache — replaces optimistic values with real DB data
        queryClient.setQueryData(['agentWorkload', agencyId], { agents: mapped });
      } catch {
        // Fallback: if fresh fetch fails, invalidate normally
        queryClient.invalidateQueries({ queryKey: ['agentWorkload'] });
      }

      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('lead-assigned'));
    },
  });
};
